import React, { useMemo, useState } from 'react';
import { WORLD_PATH, MAP_WIDTH, MAP_HEIGHT, projectPoint } from './world-map';

/**
 * VERAX: Postura y origen
 *
 * Las dos preguntas que un directivo hace al abrir la consola: cómo está mi
 * organización, y desde dónde me atacan. Todo lo demás del dashboard es
 * operación diaria del analista; esto es lo que se muestra en comité.
 */

export type Health = 'LIMPIO' | 'VIGILANDO' | 'ATENCION' | 'CRITICO' | 'SIN_LINEA_BASE';

export interface AssetHealth {
  id: string;
  nombre: string;
  detalle?: string;
  pais?: string;
  paisNombre?: string;
  estado: Health;
  criticas: number;
  altas: number;
  totalAbiertas: number;
  frenteAbierto?: string;
  ultimaRevision?: string;
  proximaRevision?: string;
}

export interface OriginBucket {
  iso?: string;
  pais: string;
  lat?: number;
  lon?: number;
  amenazas: number;
  criticas: number;
  inferidas: number;
  /** Casos registrados de junio a septiembre alojados en este país. */
  casos?: number;
}

export interface Overview {
  resumen: {
    estadoGeneral: Health;
    marcasVigiladas: number;
    directivosVigilados: number;
    marcasComprometidas: number;
    directivosSuplantados: number;
    criticas: number;
    enAtencion: number;
    paisesDeOrigen: number;
    amenazasSinUbicar: number;
  };
  marcas: AssetHealth[];
  directivos: AssetHealth[];
  origen: {
    paises: OriginBucket[];
    destinos: { iso: string; pais: string; lat?: number; lon?: number; marcas: number }[];
    registradores: { nombre: string; amenazas: number }[];
    plataformas: { nombre: string; perfiles: number }[];
    sinUbicar: number;
    pendientesDeUbicar?: number;
    casosRegistrados?: number;
    casosSinUbicar?: number;
  };
  actas: ScanReceipt[];
}

/* ------------------------------------------------------------------ */
/* Semáforo                                                            */
/* ------------------------------------------------------------------ */

// La severidad nunca se comunica solo por color: color + forma + texto.
// Un 8% de los hombres tiene algún grado de daltonismo y los comités de
// seguridad son mayoritariamente masculinos.
const HEALTH: Record<Health, { label: string; dot: string; ring: string; text: string; icon: string }> = {
  CRITICO:         { label: 'Suplantado',      dot: 'bg-red-500',    ring: 'border-red-800 bg-red-950/40',        text: 'text-red-300',    icon: '' },
  ATENCION:        { label: 'Requiere revisión', dot: 'bg-orange-400', ring: 'border-orange-800/70 bg-orange-950/25', text: 'text-orange-300', icon: '▲' },
  VIGILANDO:       { label: 'Bajo vigilancia', dot: 'bg-blue-400',   ring: 'border-blue-900/70 bg-blue-950/20',   text: 'text-blue-300',   icon: '◆' },
  LIMPIO:          { label: 'Limpio',          dot: 'bg-green-400',  ring: 'border-green-900/60 bg-green-950/20', text: 'text-green-300',  icon: '✓' },
  SIN_LINEA_BASE:  { label: 'Sin línea base',  dot: 'bg-gray-500',   ring: 'border-gray-800 bg-gray-900',         text: 'text-gray-400',   icon: '○' },
};

export const HealthPill: React.FC<{ estado: Health; compact?: boolean }> = ({ estado, compact }) => {
  const h = HEALTH[estado];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'} font-semibold ${h.text} bg-black/30`}>
      <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`} />
      {h.icon} {h.label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Postura: marcas y directivos                                        */
/* ------------------------------------------------------------------ */

/**
 * Bandera a partir del ISO 3166-1 alfa-2: dos indicadores regionales Unicode.
 * Sin imágenes, sin librería de iconos, sin una petición más.
 */
const flag = (iso?: string): string => {
  if (!iso || iso.length !== 2) return '';
  return String.fromCodePoint(
    ...iso.toUpperCase().split('').map(c => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const CountryChip: React.FC<{ iso?: string; nombre?: string }> = ({ iso, nombre }) =>
  iso ? (
    <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-800/80 border border-gray-700 rounded px-1.5 py-0.5"
      title={`Empresa con operación en ${nombre || iso}`}>
      <span aria-hidden>{flag(iso)}</span>{iso.toUpperCase()}
    </span>
  ) : null;

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');

const AssetRow: React.FC<{ a: AssetHealth; onClick?: () => void; avatar?: boolean }> = ({ a, onClick, avatar }) => {
  const h = HEALTH[a.estado];
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:brightness-125 ${h.ring}`}
    >
      {avatar ? (
        <span className={`shrink-0 w-9 h-9 rounded-full bg-gray-800 border border-gray-700 grid place-items-center text-xs font-bold ${h.text}`}>
          {initials(a.nombre)}
        </span>
      ) : (
        <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${h.dot}`} />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold truncate">{a.nombre}</span>
          <CountryChip iso={a.pais} nombre={a.paisNombre} />
        </span>
        {a.detalle && (
          <span className="block text-[11px] text-gray-500 truncate">{a.detalle}</span>
        )}
        {a.frenteAbierto && (
          <span className="block text-[11px] text-gray-400 truncate">
            suplantado desde <span className="font-mono text-gray-300">{a.frenteAbierto}</span>
          </span>
        )}
      </span>
      <span className="shrink-0 text-right">
        <HealthPill estado={a.estado} compact />
        {a.totalAbiertas > 0 && (
          <span className="block text-[11px] text-gray-500 mt-0.5 tabular-nums">
            {a.totalAbiertas} {a.totalAbiertas === 1 ? 'caso abierto' : 'casos abiertos'}
          </span>
        )}
      </span>
    </button>
  );
};

export const PosturePanel: React.FC<{
  overview: Overview;
  onMarca?: (id: string) => void;
  onDirectivos?: () => void;
}> = ({ overview, onMarca, onDirectivos }) => {
  const { marcas, directivos, resumen } = overview;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
      <section className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <header className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-200">Marcas vigiladas</h3>
          <span className="text-[11px] text-gray-500">
            {resumen.marcasComprometidas > 0
              ? `${resumen.marcasComprometidas} con suplantación activa`
              : `${resumen.marcasVigiladas} bajo vigilancia`}
          </span>
        </header>
        <div className="space-y-2">
          {marcas.map(m => <AssetRow key={m.id} a={m} onClick={() => onMarca?.(m.nombre)} />)}
          {marcas.length === 0 && (
            <p className="text-sm text-gray-500 py-6 text-center">
              Aún no hay ninguna marca registrada. Empieza en Administración.
            </p>
          )}
        </div>
      </section>

      <section className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <header className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-200">Directivos vigilados</h3>
          <span className="text-[11px] text-gray-500">
            {resumen.directivosSuplantados > 0
              ? `${resumen.directivosSuplantados} con perfil suplantado`
              : `${resumen.directivosVigilados} personas`}
          </span>
        </header>
        <div className="space-y-2 max-h-[22rem] overflow-y-auto pr-1">
          {directivos.map(d => <AssetRow key={d.id} a={d} avatar onClick={onDirectivos} />)}
          {directivos.length === 0 && (
            <p className="text-sm text-gray-500 py-6 text-center">
              Ninguna persona vigilada todavía. El fraude del CEO entra por aquí.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Mapa de origen                                                      */
/* ------------------------------------------------------------------ */

export const OriginMap: React.FC<{ origen: Overview['origen'] }> = ({ origen }) => {
  const [hover, setHover] = useState<OriginBucket | null>(null);
  const puntos = useMemo(
    () => origen.paises.filter(p => p.lat !== undefined && p.lon !== undefined),
    [origen.paises]
  );
  // Peso de cada país: amenazas vivas más casos registrados.
  const peso = (p: OriginBucket) => p.amenazas + (p.casos ?? 0);
  const maximo = Math.max(1, ...puntos.map(peso));
  const total = origen.paises.reduce((n, p) => n + p.amenazas, 0);
  const totalCasos = origen.paises.reduce((n, p) => n + (p.casos ?? 0), 0);
  // Rojo: hay una crítica viva. Ámbar: amenazas vivas. Gris azulado: solo casos registrados.
  const colorDe = (p: OriginBucket) => (p.criticas > 0 ? '#ef4444' : p.amenazas > 0 ? '#f59e0b' : '#8ea3c0');
  const destinos = useMemo(
    () => (origen.destinos ?? []).filter(d => d.lat !== undefined && d.lon !== undefined),
    [origen.destinos]
  );
  const anclaje = destinos[0];

  /**
   * Encuadre automático.
   *
   * Un mapa mundial con tres puntos en Colombia es un mapa casi vacío: el
   * lector tiene que buscar dónde está la acción antes de leer nada. La vista
   * se ajusta a la caja que contiene todos los puntos, con margen, y se acerca
   * o se aleja según la dispersión real de la campaña.
   *
   * Es el mapa el que se mueve hacia los datos, no el lector.
   */
  const [autoZoom, setAutoZoom] = useState(true);
  const viewBox = useMemo(() => {
    const completo = `0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`;
    if (!autoZoom) return completo;

    const coords = [...puntos, ...destinos]
      .map(p => projectPoint(p.lat!, p.lon!));
    if (coords.length === 0) return completo;

    let minX = Math.min(...coords.map(c => c[0]));
    let maxX = Math.max(...coords.map(c => c[0]));
    let minY = Math.min(...coords.map(c => c[1]));
    let maxY = Math.max(...coords.map(c => c[1]));

    // Margen generoso: los puntos nunca deben tocar el borde, y el contexto
    // geográfico alrededor es lo que hace legible la ubicación.
    const margen = Math.max((maxX - minX) * 0.5, (maxY - minY) * 0.5, 55);
    minX -= margen; maxX += margen; minY -= margen; maxY += margen;

    let w = maxX - minX;
    let h = maxY - minY;

    // Se conserva la proporción del lienzo para que el mapa no se deforme.
    const proporcion = MAP_WIDTH / MAP_HEIGHT;
    if (w / h > proporcion) { const nh = w / proporcion; minY -= (nh - h) / 2; h = nh; }
    else { const nw = h * proporcion; minX -= (nw - w) / 2; w = nw; }

    // Nunca más allá de los bordes del mundo, y nunca más grande que el mundo.
    if (w > MAP_WIDTH) { minX = 0; w = MAP_WIDTH; h = MAP_HEIGHT; minY = 0; }
    else {
      minX = Math.max(0, Math.min(minX, MAP_WIDTH - w));
      minY = Math.max(0, Math.min(minY, MAP_HEIGHT - h));
    }

    return `${minX.toFixed(1)} ${minY.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)}`;
  }, [autoZoom, puntos, destinos]);

  // Cuánto se ha acercado la vista: los trazos y los puntos se compensan para
  // que no engorden al ampliar.
  const escala = useMemo(() => {
    const partes = viewBox.split(' ').map(Number);
    return partes.length === 4 && partes[2] > 0 ? MAP_WIDTH / partes[2] : 1;
  }, [viewBox]);

  const region = useMemo(() => {
    const paises = Array.from(new Set([...puntos.map(p => p.pais), ...destinos.map(d => d.pais)]));
    if (paises.length === 0) return null;
    if (paises.length === 1) return paises[0];
    if (paises.length <= 3) return paises.join(', ');
    return `${paises.length} países`;
  }, [puntos, destinos]);

  return (
    <section className="mb-8">
      <header className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">
            Desde dónde te suplantan
            {region && <span className="ml-2 text-sm font-normal text-gray-500">· {region}</span>}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-3xl">
            País donde está alojada la infraestructura de cada sitio falso, resuelto por RDAP. Incluye las
            amenazas que VERAX vigila hoy y los casos registrados de junio a septiembre. Si el hosting no
            responde, se ubica por el dominio de país y se marca como estimado.
          </p>
        </div>
        {puntos.length > 0 && (
          <button onClick={() => setAutoZoom(z => !z)}
            className="shrink-0 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 px-3 py-1.5 rounded">
            {autoZoom ? 'Ver el mundo entero' : 'Acercar a la región'}
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
        <div className="relative bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
          <svg viewBox={viewBox} className="w-full block transition-[view-box] duration-500" role="img"
            aria-label={`Mapa con ${puntos.length} países de origen`}>
            <defs>
              <radialGradient id="verax-glow">
                <stop offset="0%" stopColor="#f87171" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x={0} y={0} width={MAP_WIDTH} height={MAP_HEIGHT} fill="#0b1220" />
            <path d={WORLD_PATH} fill="#18233a" stroke="#233149" strokeWidth={0.4 / escala} />

            {/* Un arco por origen hacia el país del cliente. Se dibujan primero
                para que queden por debajo de los puntos. */}
            {anclaje && puntos.map(p => {
              const [x1, y1] = projectPoint(p.lat!, p.lon!);
              const [x2, y2] = projectPoint(anclaje.lat!, anclaje.lon!);
              const mx = (x1 + x2) / 2;
              const my = (y1 + y2) / 2;
              const dx = x2 - x1, dy = y2 - y1;
              const dist = Math.hypot(dx, dy) || 1;
              // Control perpendicular al segmento: curva el arco sin cruzar el punto medio.
              const cx = mx - (dy / dist) * dist * 0.18;
              const cy = my + (dx / dist) * dist * 0.18;
              const critico = p.criticas > 0;
              return (
                <path key={`arco-${p.iso}`} d={`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
                  fill="none"
                  stroke={colorDe(p)}
                  strokeOpacity={0.35}
                  strokeWidth={(critico ? 1.1 : 0.7) / escala}
                  strokeLinecap="round" />
              );
            })}

            {/* Dónde está el cliente */}
            {destinos.map(d => {
              const [x, y] = projectPoint(d.lat!, d.lon!);
              return (
                <g key={`destino-${d.iso}`}>
                  <circle cx={x} cy={y} r={7 / escala} fill="none" stroke="#38bdf8" strokeWidth={1.1 / escala} strokeOpacity={0.7} />
                  <circle cx={x} cy={y} r={3 / escala} fill="#38bdf8" />
                  <title>{`Tu operación: ${d.pais} · ${d.marcas} ${d.marcas === 1 ? 'marca vigilada' : 'marcas vigiladas'}`}</title>
                </g>
              );
            })}

            {puntos.map(p => {
              const [x, y] = projectPoint(p.lat!, p.lon!);
              const r = (3 + (peso(p) / maximo) * 9) / escala;
              const critico = p.criticas > 0;
              const color = colorDe(p);
              return (
                <g key={p.iso} onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}>
                  <circle cx={x} cy={y} r={r * 2.6} fill="url(#verax-glow)" />
                  <circle cx={x} cy={y} r={r}
                    fill={color}
                    fillOpacity={0.85}
                    stroke={color}
                    strokeWidth={0.8 / escala}
                    strokeDasharray={p.amenazas > 0 && p.inferidas === p.amenazas ? '2 2' : undefined} />
                  {critico && (
                    <circle cx={x} cy={y} r={r} fill="none" stroke="#ef4444" strokeWidth={1 / escala}>
                      <animate attributeName="r" values={`${r};${r * 3}`} dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.7;0" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>

          {hover && (
            <div className="absolute top-3 left-3 bg-black/85 border border-gray-700 rounded px-3 py-2 text-xs pointer-events-none">
              <p className="font-semibold">{hover.pais}</p>
              {hover.amenazas > 0 && (
                <p className="text-gray-400">
                  {hover.amenazas} {hover.amenazas === 1 ? 'amenaza actual' : 'amenazas actuales'}
                  {hover.criticas > 0 && <span className="text-red-400"> · {hover.criticas} crítica{hover.criticas > 1 ? 's' : ''}</span>}
                </p>
              )}
              {(hover.casos ?? 0) > 0 && (
                <p className="text-gray-400">{hover.casos} {hover.casos === 1 ? 'caso registrado' : 'casos registrados'} (jun a sep)</p>
              )}
              {hover.inferidas > 0 && (
                <p className="text-gray-600 mt-0.5">{hover.inferidas} ubicada{hover.inferidas > 1 ? 's' : ''} por dominio de país</p>
              )}
            </div>
          )}

          <div className="absolute bottom-3 left-3 flex items-center gap-4 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> con crítica</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> sin crítica</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#8ea3c0' }} /> casos jun a sep</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full border border-dashed border-amber-400" /> estimado</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-400" /> tu operación</span>
          </div>

          {puntos.length === 0 && (
            <div className="absolute inset-0 grid place-items-center bg-gray-950/70">
              <div className="text-center px-6">
                <p className="text-gray-300 text-sm">Todavía no hay sitios ubicados.</p>
                <p className="text-gray-600 text-xs mt-1">
                  {(origen.pendientesDeUbicar ?? 0) > 0
                    ? 'VERAX está consultando el hosting de las amenazas detectadas. Recargue en un minuto.'
                    : 'El mapa se llena cuando VERAX encuentra un sitio falso y resuelve dónde está alojado.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-3">Países de origen</h4>
            {origen.paises.slice(0, 6).map(p => (
              <div key={p.iso} className="mb-2.5 last:mb-0">
                <div className="flex justify-between text-sm mb-1">
                  <span className="truncate">{p.pais}</span>
                  <span className="tabular-nums text-gray-400 shrink-0 ml-2">
                    {p.amenazas > 0 && `${p.amenazas} ${p.amenazas === 1 ? 'actual' : 'actuales'}`}
                    {p.amenazas > 0 && (p.casos ?? 0) > 0 && ' · '}
                    {(p.casos ?? 0) > 0 && `${p.casos} ${p.casos === 1 ? 'caso' : 'casos'}`}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-800 rounded overflow-hidden">
                  <div className="h-full" style={{ width: `${(peso(p) / maximo) * 100}%`, background: colorDe(p) }} />
                </div>
              </div>
            ))}
            {origen.paises.length === 0 && <p className="text-sm text-gray-500">Sin datos todavía.</p>}
            {(origen.pendientesDeUbicar ?? 0) > 0 && (
              <p className="text-[11px] text-gray-500 mt-3">
                {origen.pendientesDeUbicar} {origen.pendientesDeUbicar === 1 ? 'amenaza en consulta' : 'amenazas en consulta'} de hosting. Aparecen en el mapa al recargar.
              </p>
            )}
            {origen.sinUbicar > 0 && (
              <p className="text-[11px] text-gray-600 mt-2">
                {origen.sinUbicar} {origen.sinUbicar === 1 ? 'amenaza' : 'amenazas'} sin ubicar: el dominio no resuelve a ninguna IP.
                Es normal en dominios recién registrados que aún no están armados.
              </p>
            )}
            {(total > 0 || totalCasos > 0) && (
              <p className="text-[11px] text-gray-600 mt-2">
                {total > 0 && `${total} ${total === 1 ? 'amenaza actual' : 'amenazas actuales'}`}
                {total > 0 && totalCasos > 0 && ' y '}
                {totalCasos > 0 && `${totalCasos} ${totalCasos === 1 ? 'caso registrado' : 'casos registrados'}`}
                {' '}en {origen.paises.length} {origen.paises.length === 1 ? 'país' : 'países'}.
                {(origen.casosSinUbicar ?? 0) > 0 && ` ${origen.casosSinUbicar} casos sin país conocido (ya retirados o sin respuesta del registro).`}
              </p>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Registradores usados</h4>
            <p className="text-[11px] text-gray-600 mb-3">La otra vía de takedown, y la única cuando el sitio todavía no está armado.</p>
            {origen.registradores.map(r => (
              <div key={r.nombre} className="flex justify-between text-sm py-1 border-b border-gray-800/60 last:border-0">
                <span className="truncate pr-2">{r.nombre}</span>
                <span className="tabular-nums text-gray-400">{r.amenazas}</span>
              </div>
            ))}
            {origen.registradores.length === 0 && <p className="text-sm text-gray-500">Sin datos todavía.</p>}
          </div>

          {origen.plataformas.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Suplantación de personas</h4>
              <p className="text-[11px] text-gray-600 mb-3">Plataformas donde aparecen perfiles que imitan a tus directivos.</p>
              {origen.plataformas.map(p => (
                <div key={p.nombre} className="flex justify-between text-sm py-1 border-b border-gray-800/60 last:border-0">
                  <span className="capitalize">{p.nombre.toLowerCase()}</span>
                  <span className="tabular-nums text-gray-400">{p.perfiles}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Acta de barrido, la prueba del trabajo hecho                       */
/* ------------------------------------------------------------------ */

export interface ScanReceipt {
  empresa?: string;
  iniciado?: string;
  terminado?: string;
  // Todas las cifras son opcionales a propósito: el panel debe pintar algo
  // razonable con un acta parcial en vez de romperse.
  duracionMs?: number;
  candidatos?: number;
  resueltos?: number;
  existen?: number;
  descartadosPorSonda?: number;
  verificadosConNavegador?: number;
  amenazas?: number;
  fuentes?: Record<string, { descripcion?: string; [k: string]: any }> | null;
  estado?: string;
}

/**
 * Formateadores tolerantes a datos incompletos.
 *
 * Un acta puede llegar sin una cifra: un barrido de una versión anterior del
 * esquema, un backend a medio migrar, un scan que falló a mitad. Ninguna de
 * esas situaciones justifica tumbar el dashboard entero, que es exactamente lo
 * que hace un `undefined.toLocaleString()` dentro de un render.
 */
const num = (n?: number | null) => (typeof n === 'number' && isFinite(n) ? n : 0).toLocaleString('es-CO');

const duracion = (ms?: number | null) => {
  if (typeof ms !== 'number' || !isFinite(ms) || ms <= 0) return '-';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${s % 60} s`;
};

const fechaLarga = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

/**
 * Lo que el sistema hizo, en cifras verificables.
 *
 * Un barrido sin hallazgos es el resultado más frecuente de este producto y el
 * más difícil de defender ante quien lo paga: sin estas cifras, "cero amenazas"
 * es indistinguible de "el sistema no corrió". Por eso el panel gana peso
 * justamente cuando no hay nada que reportar, en vez de esconderse.
 */
export const ScanReceiptPanel: React.FC<{ actas: ScanReceipt[]; hayAmenazas: boolean }> = ({
  actas, hayAmenazas
}) => {
  const [abierta, setAbierta] = useState<string | null>(null);
  if (!Array.isArray(actas) || actas.length === 0) return null;

  return (
    <section className="mb-8">
      <header className="mb-3">
        <h3 className="text-lg font-semibold">Qué hizo VERAX en el último barrido</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {hayAmenazas
            ? 'El trabajo detrás de los hallazgos de arriba, en cifras verificables.'
            : 'No se encontraron amenazas. Esto es lo que se revisó para poder afirmarlo, ' +
              'un silencio sin cifras no se distingue de un sistema apagado.'}
        </p>
      </header>

      <div className="space-y-3">
        {actas.map((a, idx) => {
          const clave = a.empresa || `acta-${idx}`;
          const abierto = abierta === clave;
          const amenazas = a.amenazas ?? 0;
          const sonda = a.descartadosPorSonda ?? 0;
          const navegador = a.verificadosConNavegador ?? 0;
          // Un acta sin cifras NO es un acta con ceros. Pintar "0 candidatos"
          // sería afirmar que no se revisó nada, que es justo lo contrario de
          // lo que este panel existe para demostrar. Se dice lo que pasa.
          const sinMedicion = [a.candidatos, a.resueltos, a.existen]
            .every(v => typeof v !== 'number');
          const pasos = [
            { n: a.candidatos, t: 'candidatos generados', d: 'Permutaciones de la marca: homoglifos, combosquatting, TLD, guiones y subdominios engañosos.' },
            { n: a.resueltos, t: 'consultados en DNS', d: 'Solo los prioritarios. Resolver todo el espacio cuesta mucho y aporta poco.' },
            { n: a.existen, t: 'existen de verdad', d: 'Los que resolvieron a una dirección IP. El resto están libres o sin registrar.' },
            { n: sonda, t: 'descartados sin navegador', d: 'Una petición HTTP de unos KB bastó para saber que no imitan la marca.' },
            { n: navegador, t: 'verificados con navegador', d: 'Captura completa, hashes perceptuales y comparación contra la línea base.' },
          ];
          return (
            <div key={clave} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-semibold">{a.empresa || 'Marca sin nombre'}</p>
                  <p className="text-[11px] text-gray-500">
                    {fechaLarga(a.terminado ?? a.iniciado)} · duró {duracion(a.duracionMs)}
                    {a.estado && a.estado !== 'COMPLETED' && <span className="text-yellow-400"> · {a.estado}</span>}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  amenazas > 0 ? 'bg-red-950 text-red-300' : 'bg-green-950 text-green-300'
                }`}>
                  {amenazas > 0
                    ? `${amenazas} ${amenazas === 1 ? 'amenaza' : 'amenazas'}`
                    : '✓ sin suplantaciones'}
                </span>
              </div>

              {a.fuentes?.error && (
                <p className="px-4 pt-3 pb-3 text-[11px] text-red-300 bg-red-950/25 border-b border-red-900/40">
                  <strong>El barrido no pudo completarse.</strong>{' '}
                  {a.fuentes.error.motivo}
                  {a.fuentes.error.etapa && a.fuentes.error.etapa !== 'indeterminada' && (
                    <> · falló en la {a.fuentes.error.etapa}</>
                  )}
                </p>
              )}

              {sinMedicion && !a.fuentes?.error && (
                <p className="px-4 pt-3 text-[11px] text-yellow-300/90 bg-yellow-950/20 border-b border-yellow-900/40 pb-3">
                  Este barrido no trae las cifras del acta. Suele significar que el backend sigue
                  corriendo con el cliente de base de datos anterior: reinícialo después de
                  <span className="font-mono"> npx prisma generate</span>. Los barridos siguientes ya
                  las registran.
                </p>
              )}

              {/* El embudo: de todo el espacio posible a lo que de verdad se miró */}
              <div className={`px-4 py-4 grid grid-cols-2 md:grid-cols-5 gap-3 ${sinMedicion ? 'opacity-40' : ''}`}>
                {pasos.map((p, i) => (
                  <div key={p.t} className="relative" title={p.d}>
                    <p className="text-2xl font-bold tabular-nums leading-none">{num(p.n)}</p>
                    <p className="text-[11px] text-gray-500 mt-1 leading-tight">{p.t}</p>
                    {i < pasos.length - 1 && (
                      <span className="hidden md:block absolute top-2 -right-2 text-gray-700">›</span>
                    )}
                  </div>
                ))}
              </div>

              {!sinMedicion && sonda > 0 && (
                <p className="px-4 pb-3 text-[11px] text-gray-500">
                  La sonda previa evitó{' '}
                  <strong className="text-gray-400">
                    {Math.round((sonda / (sonda + navegador)) * 100)}%
                  </strong>{' '}
                  de los arranques de navegador. Ese porcentaje es el costo que no se factura al cliente.
                </p>
              )}

              {a.fuentes && typeof a.fuentes === 'object' && (
                <div className="border-t border-gray-800">
                  <button onClick={() => setAbierta(abierto ? null : clave)}
                    className="w-full px-4 py-2.5 flex items-center justify-between text-xs text-gray-400 hover:text-gray-200">
                    <span>Fuentes consultadas y su resultado</span>
                    <span className="text-gray-600">{abierto ? 'ocultar' : 'ver detalle'}</span>
                  </button>
                  {abierto && (
                    <div className="px-4 pb-4 space-y-2">
                      {Object.entries(a.fuentes ?? {}).map(([fuente, valor]) => (
                        <div key={fuente} className="bg-gray-950/60 border border-gray-800 rounded p-3">
                          <p className="text-xs font-semibold capitalize">{fuente}</p>
                          {valor?.descripcion && (
                            <p className="text-[11px] text-gray-500 mt-0.5">{valor.descripcion}</p>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                            {Object.entries(valor ?? {})
                              .filter(([k]) => k !== 'descripcion')
                              .map(([k, v]) => (
                                <span key={k} className="text-[11px] text-gray-400">
                                  <span className="text-gray-600">{k}:</span>{' '}
                                  {Array.isArray(v) ? v.join(', ') : String(v)}
                                </span>
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
